/* eslint-disable */
import React from "react";
import { Link, useLocation } from "react-router-dom";
import { MdOutlineSpaceDashboard } from "react-icons/md";
import { useLanguage } from "context/LanguageContext";
import { authService } from "services/authService";
// chakra imports

export const SidebarLinks = (props: { routes: RoutesType[]; collapsed?: boolean }): JSX.Element => {
  // Chakra color mode
  let location = useLocation();
  const { t } = useLanguage();

  const { routes, collapsed } = props;
  const currentRole = authService.getStoredUser()?.role;

  // verifies if routeName is the one active (in browser input)
  const activeRoute = (route: RoutesType) => {
    if (!route.layout || !route.path) return false;

    const normalizedPath = route.path.replace(/\/\*$/, "");
    const fullPath = `${route.layout}/${normalizedPath}`;
    const layoutPrefix = `${route.layout}/`;
    const pathAfterLayout = location.pathname.startsWith(layoutPrefix)
      ? location.pathname.slice(layoutPrefix.length)
      : "";
    const currentTopSegment = pathAfterLayout.split("/")[0];
    const routeTopSegment = normalizedPath.split("/")[0];

    // Session detail page can be opened from Class detail (`/classes-mgmt/:id/sessions/:id`).
    // In that case, only highlight the Buoi hoc menu (`/admin/sessions`).
    if (
      location.pathname.includes("/classes-mgmt/") &&
      location.pathname.includes("/sessions/")
    ) {
      return normalizedPath === "sessions";
    }

    // Keep top-level section highlight strict to the first URL segment
    // so `/admin/user-sessions` won't also activate `/admin/sessions`.
    if (currentTopSegment) {
      return currentTopSegment === routeTopSegment;
    }

    return location.pathname.startsWith(fullPath);
  };

  const createLinks = (routes: RoutesType[]) => {
    return routes.map((route, index) => {
      if (route.hidden) {
        return null;
      }

      if (
        route.allowedRoles &&
        route.allowedRoles.length > 0 &&
        (!currentRole || !route.allowedRoles.includes(currentRole))
      ) {
        return null;
      }

      // Profile settings is available in the bottom user menu, so we hide it from the main sidebar.
      if (route.name === "nav.settings") {
        return null;
      }

      // Change password is also reachable from the bottom user menu, so we hide it from the main sidebar.
      if (route.name === "nav.changePassword") {
        return null;
      }

      if (route.layout && route.path) {
        const isActive = activeRoute(route);
        const hasChildren = route.children && route.children.length > 0;
        return (
          <React.Fragment key={index}>
            <Link to={route.layout + "/" + route.path.replace(/\/\*$/, "")}>
              <div className={`relative mb-1 flex hover:cursor-pointer group ${collapsed ? "justify-center" : ""}`}>
                <li
                  className={`my-[3px] flex cursor-pointer items-center ${collapsed ? "justify-center px-0 py-2" : "px-6 py-2"} rounded-lg mx-3 transition-colors ${
                    isActive
                      ? "bg-brand-50 dark:bg-brand-500/15"
                      : "hover:bg-surface-hover"
                  }`}
                  style={collapsed ? { width: "48px", justifyContent: "center" } : {}}
                >
                  <span
                    className={`text-xl ${
                      isActive
                        ? "text-brand-500"
                        : "text-ink-muted"
                    }`}
                  >
                    {route.icon ? route.icon : <MdOutlineSpaceDashboard className="h-6 w-6" />}
                  </span>
                  {!collapsed && (
                    <div className="ml-3 flex min-w-0 flex-1 items-center gap-2">
                      <p
                        className={`leading-1 flex whitespace-nowrap text-sm ${
                          isActive
                              ? "font-semibold text-ink"
                              : "font-medium text-ink-muted"
                        }`}
                      >
                        {t(route.name)}
                      </p>
                      {route.upcoming ? (
                        <span className="inline-flex shrink-0 whitespace-nowrap rounded-full bg-warning-50 px-2 py-0.5 text-[10px] font-semibold text-warning-700 dark:bg-warning-500/15 dark:text-amber-300">
                          {t("common.soon")}
                        </span>
                      ) : null}
                    </div>
                  )}
                </li>
                {/* Active indicator */}
                {isActive && !collapsed ? (
                  <div className="absolute right-0 top-1/2 -translate-y-1/2 h-8 w-1 rounded-lg bg-brand-500 dark:bg-brand-400" />
                ) : null}
                {/* Tooltip when collapsed */}
                {collapsed && (
                  <div className="pointer-events-none absolute left-full top-1/2 z-50 ml-2 -translate-y-1/2 whitespace-nowrap rounded-md bg-navy-700 px-3 py-1.5 text-xs font-medium text-white opacity-0 shadow-lg transition-opacity group-hover:opacity-100">
                    {t(route.name)}
                  </div>
                )}
              </div>
            </Link>

            {/* Center context tag */}
            {route.contextLabel && !collapsed ? (
              <div className="mb-2 mx-6 rounded-lg border border-warning-200 bg-warning-50 px-3 py-2 dark:bg-warning-500/15 dark:border-warning-500/30">
                <p className="truncate text-xs font-semibold text-warning-700 dark:text-amber-300">
                  {route.contextLabel}
                </p>
              </div>
            ) : null}

            {/* Sub-nav children — only when parent is active and not collapsed */}
            {isActive && hasChildren && !collapsed && (
              <ul className="mb-2 ml-6 mr-3 space-y-0.5 border-l-2 border-brand-200 pl-3">
                {route.children!.map((child, ci) => {
                  const childActive = location.pathname === child.href || location.pathname.startsWith(child.href + "/");
                  return (
                    <li key={ci}>
                      <Link
                        to={child.href}
                        className={`flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm transition-colors ${
                          childActive
                            ? "bg-brand-50 font-semibold text-brand-500 dark:bg-brand-500/15 dark:text-brand-300"
                            : "font-medium text-ink-muted hover:bg-surface-hover hover:text-ink"
                        }`}
                      >
                        <span className="text-base shrink-0">{child.icon}</span>
                        <span className="whitespace-nowrap">{t(child.name)}</span>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            )}
          </React.Fragment>
        );
      }
    });
  };
  // BRAND
  return <>{createLinks(routes)}</>;
};

export default SidebarLinks;
